import React, { useContext, useEffect, useState } from "react";
import {
    Avatar,
    Button,
    Checkbox,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListItem,
    ListItemAvatar
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import { formatName, stringAvatar, stringToColor } from "../../Helpers/TextStyling";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import { PAContext } from "../../../Services/PAContext";
import { getAllContactsFromDatabase } from "../../Restricted/Contacts/BrowserDatabaseFunctions";

function LodgingDialogEditAttendees({ isOpen, onClose, eventLodging, setEventLodging }) {
    const { PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray } = useContext(PAContext);
    const { SampleAttendees, authenticated } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;
        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) {
                    setDbContacts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated]);

    const [attendeesReferenceArray, setAttendeesReferenceArray] = useState(
        authenticated ? dbContacts : SampleAttendees
    );

    useEffect(() => {
        setAttendeesReferenceArray(
            authenticated ? dbContacts : SampleAttendees
        );
    }, [authenticated, dbContacts, SampleAttendees]);

    const [selectedAttendeesArray, setSelectedAttendeesArray] = useState(eventLodging.attendees);
    const [showSelector, setShowSelector] = useState(false);
    const [tempSelected, setTempSelected] = useState([]);

    // Sync state when lodging item changes externally
    useEffect(() => {
        if (eventLodging.attendees) {
            setSelectedAttendeesArray(eventLodging.attendees);
        }
    }, [eventLodging]);

    // Filter and group attendees
    const groupedAttendees = {
        'Event Attendees': usePA_GlobalStore.getState().PA_GlobalObject.Planning.attendees
            .filter(a => !a.planner && !selectedAttendeesArray.some(sel => sel.id === a.id)),

        'Other Contacts': attendeesReferenceArray
            .filter(a =>
                !a.planner &&
                !usePA_GlobalStore.getState().PA_GlobalObject.Planning.attendees.some(ea => ea.id === a.id) &&
                !selectedAttendeesArray.some(sel => sel.id === a.id) &&
                !a.isSample
            ),

        'Sample Attendees': attendeesReferenceArray
            .filter(a =>
                !a.planner &&
                !usePA_GlobalStore.getState().PA_GlobalObject.Planning.attendees.some(ea => ea.id === a.id) &&
                !selectedAttendeesArray.some(sel => sel.id === a.id) &&
                a.isSample
            ),
    };

    const toggleTemp = (id) => {
        setTempSelected((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleDoneSelectingAttendees = () => {
        const updatedSelectedAttendees = [...selectedAttendeesArray];

        tempSelected.forEach((_attendee_id) => {
            const isAlreadyInLodging = updatedSelectedAttendees.some(a => a.id === _attendee_id);
            if (!isAlreadyInLodging) {
                updatedSelectedAttendees.push({
                    id: _attendee_id,
                    cost: 0,
                    is_business_expense: false,
                    business_expense_amount_authorized: 0,
                    business_expense_amount_authorized_by: "",
                    business_expense_amount_authorized_timestamp: 0,
                    business_expense_amount: 0,
                    business_expense_paid: 0,
                    business_expense_reference_id: "",
                });
            }
        });

        if (eventLodging.assign_equal) {
            const lodgingCost = parseFloat(eventLodging.amount_total);
            const numberOfAttendees = updatedSelectedAttendees.length;

            const baseCost = parseFloat((Math.floor((lodgingCost / numberOfAttendees) * 100) / 100).toFixed(2));
            const remainder = parseFloat((lodgingCost - baseCost * numberOfAttendees).toFixed(2));

            updatedSelectedAttendees.forEach((attendee) => {
                attendee.cost = baseCost.toFixed(2);
            });

            const adminAttendee = updatedSelectedAttendees.find(att => att.role === 'admin');
            if (adminAttendee) {
                adminAttendee.cost = (baseCost + remainder).toFixed(2);
            } else if (updatedSelectedAttendees.length > 0) {
                const last = updatedSelectedAttendees[updatedSelectedAttendees.length - 1];
                last.cost = (baseCost + remainder).toFixed(2);
            }
        }

        setSelectedAttendeesArray(updatedSelectedAttendees);
        setTempSelected([]);
        setShowSelector(false);
    };

    const handleAttendeeToggle = (_attendee_id) => () => {
        const index = selectedAttendeesArray.findIndex(item => item.id === _attendee_id);
        let updatedList = [...selectedAttendeesArray];

        if (index !== -1) {
            updatedList.splice(index, 1);
        } else {
            const newAttendee = attendeesReferenceArray.find(
                (attendee) => String(attendee.id) === String(_attendee_id));

            if (!newAttendee) {
                console.error(`Attendee with ID ${_attendee_id} not found.`);
                return;
            }

            const attendeeWithCost = {
                id: _attendee_id,
                cost: 0,
                is_business_expense: false,
                business_expense_amount_authorized: 0,
                business_expense_amount_authorized_by: "",
                business_expense_amount_authorized_timestamp: 0,
                business_expense_amount: 0,
                business_expense_paid: 0,
                business_expense_reference_id: "",
            };
            updatedList.push(attendeeWithCost);
        }

        if (eventLodging["assign_equal"]) {
            const lodgingCost = parseFloat(eventLodging["amount_total"]);
            const numberOfAttendees = updatedList.length;
            const baseCostPerAttendee = parseFloat(Math.floor((lodgingCost / numberOfAttendees) * 100) / 100).toFixed(2);
            const remainder = Math.round((lodgingCost - parseFloat(baseCostPerAttendee * numberOfAttendees).toFixed(2)) * 100) / 100;

            updatedList.forEach((attendee, i) => {
                attendee.cost = baseCostPerAttendee;
                if (i === numberOfAttendees - 1) {
                    attendee.cost = (parseFloat(remainder) + parseFloat(baseCostPerAttendee)).toFixed(2);
                }
            });
        }

        setSelectedAttendeesArray(updatedList);
    };

    const handleDoneEditingAttendees = () => {
        setEventLodging(prev => ({
            ...prev,
            attendees: selectedAttendeesArray,
        }));
        onClose();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)'
                }
            }}
        >
            <DialogTitle sx={{ color: '#151269', textAlign: 'center', backgroundColor: '#f0f7ff', fontWeight: '600', p: 2 }}>
                Lodging Attendees
            </DialogTitle>
            <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: '#151269' }}>
                <i className="icon-cross" style={{ fontSize: '16px' }} />
            </IconButton>

            <DialogContent sx={{ p: 2, '&::-webkit-scrollbar': { display: 'none' } }}>
                <List dense sx={{ width: '100%', py: 0 }}>
                    {selectedAttendeesArray
                        .sort((a, b) => {
                            const a_attendee = attendeesReferenceArray.find((_att) => _att.id === a.id);
                            const b_attendee = attendeesReferenceArray.find((_att) => _att.id === b.id);

                            if (!a_attendee && !b_attendee) return 0;
                            if (!a_attendee) return 1;
                            if (!b_attendee) return -1;

                            const aHasNickname = a_attendee.nickname?.trim() || '';
                            const bHasNickname = b_attendee.nickname?.trim() || '';

                            if (aHasNickname && bHasNickname) {
                                return a_attendee.nickname.toLowerCase().localeCompare(b_attendee.nickname.toLowerCase());
                            } else {
                                return (a_attendee.fname || '').localeCompare(b_attendee.fname || '');
                            }
                        })
                        .map((_a, index) => {
                            const _attendee = PA_GlobalObject.Planning.attendees.find((_att) => _att.id === _a.id);
                            const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === _a.id);

                            if (!_attendee) return null;

                            return (
                                <div key={index}>
                                    <ListItem
                                        secondaryAction={
                                            <Checkbox
                                                edge="end"
                                                onChange={handleAttendeeToggle(_attendee.id)}
                                                checked={true}
                                                disableRipple
                                                sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
                                            />
                                        }
                                        disablePadding
                                    >
                                        <ListItemButton sx={{ py: 1.2, px: 1, borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' } }}>
                                            <ListItemAvatar sx={{ minWidth: '44px' }}>
                                                {_attendee.isSample ? (
                                                    <Chip
                                                        label="SMPL"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: "#f5f5f5",
                                                            color: "#666",
                                                            fontWeight: "700",
                                                            fontSize: "11px",
                                                            height: "20px",
                                                            borderRadius: "6px",
                                                            border: "1px solid #e0e0e0",
                                                        }}
                                                    />
                                                ) : !attendeeAvatar || !attendeeAvatar.avatar?.preview ? (
                                                    <Avatar
                                                        {...stringAvatar(`${formatName(_attendee.fname)}, ${formatName(_attendee.lname)}`)}
                                                        sx={{
                                                            boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
                                                            border: `1px solid ${stringToColor(`${formatName(_attendee.fname)}, ${formatName(_attendee.lname)}`)}`,
                                                            borderRadius: '10px',
                                                            fontWeight: '700',
                                                            backgroundColor: '#FFF',
                                                            color: '#000',
                                                            fontSize: '14px',
                                                            width: '34px',
                                                            height: '34px',
                                                        }}
                                                    />
                                                ) : (
                                                    <Avatar
                                                        src={attendeeAvatar.avatar.preview}
                                                        sx={{
                                                            boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
                                                            border: `1px solid ${stringToColor(`${formatName(_attendee.fname)}, ${formatName(_attendee.lname)}`)}`,
                                                            borderRadius: '10px',
                                                            width: '34px',
                                                            height: '34px',
                                                        }}
                                                    />
                                                )}
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={`${formatName(_attendee.lname)}, ${formatName(_attendee.fname)}`}
                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500, color: '#334155' }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                    <Divider sx={{ opacity: 0.6 }} />
                                </div>
                            );
                        })
                    }
                </List>

                <Box display="flex" justifyContent="center" mt={2.5}>
                    {!showSelector ? (
                        <Button
                            variant="outlined"
                            onClick={() => setShowSelector(true)}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, borderColors: '#1976d2', color: '#1976d2' }}
                        >
                            + Add Guest Attendees
                        </Button>
                    ) : (
                        <Box display="flex" gap={1.5}>
                            <Button
                                variant="text"
                                color="secondary"
                                onClick={() => {
                                    setShowSelector(false);
                                    setTempSelected([]);
                                }}
                                sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleDoneSelectingAttendees}
                                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, backgroundColor: '#1976d2' }}
                            >
                                Add Selection
                            </Button>
                        </Box>
                    )}
                </Box>

                <Collapse in={showSelector}>
                    <Box mt={2.5} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fcfcfc', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' }}>
                        {Object.entries(groupedAttendees).map(([group, attendees]) => (
                            attendees.length > 0 && (
                                <Box key={group}>
                                    <Typography variant="caption" sx={{ display: 'block', backgroundColor: '#f1f5f9', color: '#64748b', fontWeight: 700, px: 2, py: 0.8, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                        {group}
                                    </Typography>
                                    <List dense sx={{ py: 0 }}>
                                        {attendees
                                            .sort((a, b) =>
                                                `${formatName(a.lname)}, ${formatName(a.fname)}`.localeCompare(
                                                    `${formatName(b.lname)}, ${formatName(b.fname)}`
                                                )
                                            )
                                            .map((attendee) => (
                                                <ListItem
                                                    key={attendee.id}
                                                    secondaryAction={
                                                        <Checkbox
                                                            edge="end"
                                                            onChange={() => toggleTemp(attendee.id)}
                                                            checked={tempSelected.includes(attendee.id)}
                                                            sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
                                                        />
                                                    }
                                                    sx={{ py: 0.5, px: 2, '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.02)' } }}
                                                >
                                                    <ListItemAvatar sx={{ minWidth: '42px' }}>
                                                        {attendee.isSample ? (
                                                            <Chip label="SMPL" size="small" sx={{ height: '18px', fontSize: '10px', fontWeight: 700 }} />
                                                        ) : !attendee.avatar?.preview ? (
                                                            <Avatar
                                                                {...stringAvatar(`${formatName(attendee.fname)}, ${formatName(attendee.lname)}`)}
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    fontSize: '13px',
                                                                    color: '#000',
                                                                    backgroundColor: '#fff',
                                                                    fontWeight: 600,
                                                                    border: `1px solid ${stringToColor(`${formatName(attendee.fname)}, ${formatName(attendee.lname)}`)}`,
                                                                }}
                                                            />
                                                        ) : (
                                                            <Avatar
                                                                src={attendee.avatar.preview}
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    border: `1px solid ${stringToColor(`${formatName(attendee.fname)}, ${formatName(attendee.lname)}`)}`,
                                                                }}
                                                            />
                                                        )}
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={`${formatName(attendee.lname)}, ${formatName(attendee.fname)}`}
                                                        primaryTypographyProps={{ variant: 'body2', color: '#475569' }}
                                                    />
                                                </ListItem>
                                            ))}
                                    </List>
                                </Box>
                            )
                        ))}
                    </Box>
                </Collapse>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, backgroundColor: '#fcfcfc', borderTop: '1px solid #f0f0f0' }}>
                <Button onClick={onClose} sx={{ color: 'text.secondary', fontWeight: '600' }}>Cancel</Button>
                <Button
                    onClick={handleDoneEditingAttendees}
                    variant="contained"
                    disableElevation
                    sx={{ borderRadius: '8px', px: 3, fontWeight: '600', backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default LodgingDialogEditAttendees;